// ============================================================================
// KLINISOL CRO - Quiz Section Component (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import trainingApi from '../../services/trainingApi';
import { 
    CheckCircle, 
    XCircle, 
    Clock, 
    RotateCcw, 
    FileText, 
    ArrowRight,
    AlertCircle,
    Lightbulb 
} from 'lucide-react';

const QuizSection = ({ sectionId, moduleId, quizData: quizDataProp, passingCriteria, onComplete, isCompleted }) => {
    const navigate = useNavigate();
    
    const [quizData, setQuizData] = useState(null);
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quizStartTime] = useState(new Date().toISOString());
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
    
    // Fetch quiz questions from API
    useEffect(() => {
        const fetchQuizQuestions = async () => {
            try {
                setLoading(true);
                // trainingApi.getQuizQuestions returns response.data.data directly
                const quizResponseData = await trainingApi.getQuizQuestions(sectionId);
                
                if (quizResponseData) {
                    setQuizData(quizResponseData);
                } else {
                    console.error('Failed to load quiz questions: No data returned');
                }
            } catch (error) {
                console.error('Error fetching quiz questions:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchQuizQuestions();
    }, [sectionId]);
    
    // Initialize timer if time limit exists
    useEffect(() => {
        if (quizData?.metadata?.time_limit_minutes && !submitted) {
            setTimeRemaining(quizData.metadata.time_limit_minutes * 60);
        }
    }, [quizData, submitted]);
    
    // Countdown timer
    useEffect(() => {
        if (timeRemaining === null || submitted) return;
        
        if (timeRemaining <= 0) {
            handleSubmit(true);
            return;
        }
        
        const timer = setInterval(() => {
            setTimeRemaining(prev => prev - 1);
        }, 1000);
        
        return () => clearInterval(timer);
    }, [timeRemaining, submitted]);
    
    const handleAnswerSelect = (questionId, answer) => {
        setAnswers(prev => ({
            ...prev,
            [`question_${questionId}`]: answer
        }));
    };
    
    const handleSubmit = async (autoSubmit = false) => {
        if (!autoSubmit) {
            const unansweredCount = quizData.questions.length - Object.keys(answers).length;
            if (unansweredCount > 0) {
                const confirmSubmit = window.confirm(
                    `You have ${unansweredCount} unanswered question(s). Submit anyway?`
                );
                if (!confirmSubmit) return;
            }
        }
        
        setLoading(true);
        setShowConfirmSubmit(false);
        
        try {
            const timeSpent = Math.floor((new Date() - new Date(quizStartTime)) / 1000);
            
            const result = await trainingApi.submitQuiz({
                sectionId,
                moduleId,
                answers,
                startedAt: quizStartTime,
                timeSpent
            });
            
            setResults(result);
            setSubmitted(true);
            
            if (result.passed) {
                onComplete(true);
            }
        } catch (err) {
            console.error('Quiz submission error:', err);
            alert('Failed to submit quiz. Please try again.');
            setLoading(false);
        }
    };
    
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    const getAnsweredCount = () => Object.keys(answers).length;
    const getTotalQuestions = () => {
        if (loading || !quizData) return 0;
        return quizData.questions?.length || 0;
    };
    const getProgressPercentage = () => {
        const total = getTotalQuestions();
        return total > 0 ? (getAnsweredCount() / total) * 100 : 0;
    };
    
    // Results view
    if (submitted && results) {
        return (
            <div className="p-8 max-w-4xl mx-auto">
                {/* Result Header */}
                <div className={`rounded-2xl p-12 text-center shadow-lg border-4 mb-8 ${
                    results.passed 
                        ? 'bg-gradient-to-br from-green-100 to-green-50 border-green-500' 
                        : 'bg-gradient-to-br from-red-100 to-red-50 border-red-500'
                }`}>
                    <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center text-white shadow-lg ${
                        results.passed 
                            ? 'bg-gradient-to-br from-green-500 to-green-600' 
                            : 'bg-gradient-to-br from-red-500 to-red-600'
                    }`}>
                        {results.passed ? (
                            <CheckCircle className="w-12 h-12" />
                        ) : (
                            <XCircle className="w-12 h-12" />
                        )}
                    </div>
                    <h2 className={`text-4xl font-bold mb-4 ${
                        results.passed ? 'text-green-800' : 'text-red-800'
                    }`}>
                        {results.passed ? 'Congratulations! You Passed!' : 'Not Passed'}
                    </h2>
                    <p className={`text-lg font-medium ${
                        results.passed ? 'text-green-700' : 'text-red-700'
                    }`}>
                        {results.passed 
                            ? 'Great job! You have successfully completed this assessment.'
                            : 'Don\'t worry! Review the material and try again.'}
                    </p>
                </div>
                
                {/* Score Details */}
                <div className="bg-white rounded-2xl shadow-md p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                Your Score
                            </div>
                            <div className="text-4xl font-bold text-[#475569] mb-1">
                                {results.score}/{results.total_points}
                            </div>
                            <div className="text-xl text-gray-600 font-semibold">
                                ({results.percentage}%)
                            </div>
                        </div>
                        
                        <div className="text-center">
                            <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                Passing Score
                            </div>
                            <div className="text-4xl font-bold text-green-600">
                                {results.min_passing_score}%
                            </div>
                        </div>
                        
                        <div className="text-center">
                            <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                Attempt Number
                            </div>
                            <div className="text-4xl font-bold text-[#E91E63]">
                                #{results.attempt_number}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-4 flex-wrap">
                    {!results.passed && (
                        <button 
                            onClick={() => window.location.reload()} 
                            className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold hover:-translate-y-0.5 transition-all shadow-lg"
                        >
                            <RotateCcw className="w-5 h-5" />
                            <span>Retake Quiz</span>
                        </button>
                    )}
                    
                    {results.can_review && (
                        <button 
                            onClick={() => navigate(`/training/quiz/review/${results.attempt_id}`)}
                            className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#E91E63] to-[#C2185B] text-white rounded-xl font-semibold hover:-translate-y-0.5 transition-all shadow-lg"
                        >
                            <FileText className="w-5 h-5" />
                            <span>Review Answers</span>
                        </button>
                    )}
                    
                    {results.passed && (
                        <button 
                            onClick={() => onComplete(true)}
                            className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:-translate-y-0.5 transition-all shadow-lg"
                        >
                            <span>Continue</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    )}
                </div>
                
                {/* Tip for Failed */}
                {!results.passed && (
                    <div className="mt-8 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-6 text-center">
                        <div className="flex items-center justify-center gap-2 text-blue-800">
                            <Lightbulb className="w-6 h-6" />
                            <p className="text-base leading-relaxed">
                                <strong>Tip:</strong> Review the training material carefully and pay attention to key concepts. You can retake this quiz as many times as needed to pass.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        );
    }
    
    // Loading state
    if (loading) {
        return (
            <div className="p-8 max-w-5xl mx-auto text-center">
                <div className="bg-white rounded-2xl shadow-md p-12">
                    <div className="animate-spin w-12 h-12 border-4 border-[#475569] border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading quiz questions...</p>
                </div>
            </div>
        );
    }
    
    // No quiz data
    if (!quizData || !quizData.questions || quizData.questions.length === 0) {
        return (
            <div className="p-8 max-w-5xl mx-auto text-center">
                <div className="bg-white rounded-2xl shadow-md p-12">
                    <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No quiz questions available for this section.</p>
                </div>
            </div>
        );
    }
    
    // Quiz view
    return (
        <div className="p-8 max-w-5xl mx-auto">
            {/* Quiz Header */}
            <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                <div className="flex justify-between items-center flex-wrap gap-4">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            Quiz Assessment
                        </h3>
                        <p className="text-gray-600">
                            Answer all questions to the best of your ability. You need {passingCriteria?.min_score || 80}% to pass.
                        </p>
                    </div>
                    
                    <div className="flex gap-6 items-center">
                        <div className="text-center">
                            <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
                                Progress
                            </div>
                            <div className="text-2xl font-bold text-[#475569]">
                                {getAnsweredCount()}/{getTotalQuestions()}
                            </div>
                        </div>
                        
                        {timeRemaining !== null && (
                            <div className={`text-center px-5 py-3 rounded-xl border-2 ${
                                timeRemaining < 60 
                                    ? 'bg-gradient-to-br from-red-100 to-red-50 border-red-500' 
                                    : 'bg-gradient-to-br from-gray-100 to-gray-50 border-[#475569]'
                            }`}>
                                <div className={`text-xs font-semibold uppercase mb-1 ${
                                    timeRemaining < 60 ? 'text-red-800' : 'text-[#475569]'
                                }`}>
                                    Time Remaining
                                </div>
                                <div className={`text-2xl font-bold flex items-center gap-2 ${
                                    timeRemaining < 60 ? 'text-red-600' : 'text-[#475569]'
                                }`}>
                                    <Clock className="w-5 h-5" />
                                    {formatTime(timeRemaining)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-2 bg-gray-200 rounded-full mb-8 overflow-hidden">
                <div 
                    className="h-full bg-gradient-to-r from-[#475569] to-[#334155] rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${getProgressPercentage()}%` }}
                />
            </div>
            
            {/* Questions */}
            <div className="flex flex-col gap-6">
                {quizData?.questions?.map((question, index) => (
                    <div 
                        key={question.id} 
                        className={`bg-white rounded-2xl shadow-md p-6 transition-all ${
                            answers[`question_${question.id}`]
                                ? 'border-2 border-[#475569] shadow-lg'
                                : 'border-2 border-transparent'
                        }`}
                    >
                        <div className="flex justify-between items-center mb-4 pb-4 border-b-2 border-gray-200">
                            <span className="text-sm font-bold text-[#475569] uppercase tracking-wide">
                                Question {index + 1}
                            </span>
                            <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                                {question.points} point{question.points > 1 ? 's' : ''}
                            </span>
                        </div>
                        
                        <div className="mb-6">
                            <p className="text-lg text-gray-900 leading-relaxed font-medium">
                                {question.question}
                            </p>
                        </div>
                        
                        <div className="flex flex-col gap-3">
                            {question.options?.map((option) => (
                                <label 
                                    key={option.key}
                                    className={`flex items-center p-4 rounded-xl cursor-pointer transition-all border-2 gap-4 ${
                                        answers[`question_${question.id}`] === option.key
                                            ? 'bg-gradient-to-br from-gray-100 to-gray-50 border-[#475569]'
                                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-400'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name={`question_${question.id}`}
                                        value={option.key}
                                        checked={answers[`question_${question.id}`] === option.key}
                                        onChange={() => handleAnswerSelect(question.id, option.key)}
                                        className="w-5 h-5 cursor-pointer accent-[#475569]"
                                    />
                                    <span className="font-bold text-[#475569] text-base min-w-[24px]">
                                        {option.key}.
                                    </span>
                                    <span className="flex-1 text-gray-900 text-base leading-relaxed">
                                        {option.text}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Submit Section */}
            <div className="mt-8 bg-white rounded-2xl shadow-md p-6 text-center">
                {showConfirmSubmit ? (
                    <div>
                        <p className="mb-6 text-lg text-gray-900 font-semibold">
                            Are you sure you want to submit your quiz?
                        </p>
                        <div className="flex gap-4 justify-center flex-wrap">
                            <button 
                                onClick={() => setShowConfirmSubmit(false)}
                                className="px-8 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => handleSubmit(false)}
                                disabled={loading}
                                className={`px-8 py-3.5 rounded-xl font-semibold transition-all shadow-lg ${
                                    loading
                                        ? 'bg-gray-400 text-white cursor-not-allowed opacity-70'
                                        : 'bg-gradient-to-r from-[#475569] to-[#334155] text-white hover:-translate-y-0.5'
                                }`}
                            >
                                {loading ? 'Submitting...' : 'Yes, Submit'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <button 
                            onClick={() => setShowConfirmSubmit(true)}
                            disabled={loading || getAnsweredCount() === 0}
                            className={`px-12 py-4 rounded-xl text-lg font-bold transition-all shadow-lg ${
                                (loading || getAnsweredCount() === 0)
                                    ? 'bg-gray-400 text-white cursor-not-allowed opacity-60'
                                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:-translate-y-0.5'
                            }`}
                        >
                            Submit Quiz
                        </button>
                        
                        {getAnsweredCount() < getTotalQuestions() && (
                            <div className="mt-4 flex items-center justify-center gap-2 text-orange-600 font-semibold">
                                <AlertCircle className="w-5 h-5" />
                                <p className="text-sm">
                                    You have {getTotalQuestions() - getAnsweredCount()} unanswered question(s)
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default QuizSection;